import React from "react";
import { Button, Container, Stack, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { Add, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "src/shared/hooks";
import { blogsModel } from "src/entities/blogs";
import { modalModel } from "src/entities/modal";
import { BlogType, ModalIDs } from "src/shared/config";
import { DatePicker, Dropzone } from "src/shared/ui";
import { TagsAutocomplete } from "src/features/tagsAutocomplete";
import { RoutePaths } from "src/app/model";

export const AddBlogPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { title, description, date, section, tags } = useAppSelector(
    blogsModel.selectors.selectNewBlog,
  );

  const handleChange = (data: Partial<BlogType>) => {
    dispatch(blogsModel.actions.changeNewBlog(data));
  };

  const handleAddBlog = () => {
    dispatch(blogsModel.thunks.createBlog());
    navigate(RoutePaths.ADMIN);
  };

  const handleCreateSection = () => {
    dispatch(modalModel.actions.openModal(ModalIDs.SECTION_CREATION_MODAL));
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: 1,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Stack
        width={1}
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        useFlexGap
        padding={2}
        borderRadius={3}
        sx={{
          backgroundColor: "info.main",
        }}
      >
        <TextField
          size="small"
          label="Title"
          value={title}
          fullWidth
          onChange={(event) => handleChange({ title: event.target.value })}
        />

        <Dropzone
          uploadTitle="Upload blog image"
          onChange={(image) => handleChange({ image: image })}
          sx={{ alignSelf: "flex-start" }}
        />

        <TextField
          size="small"
          fullWidth
          label="Description"
          value={description}
          multiline
          minRows={8}
          maxRows={8}
          onChange={(event) =>
            handleChange({ description: event.target.value })
          }
        />

        <DatePicker
          value={moment(date)}
          onChange={(value) =>
            handleChange({ date: value.format("MMMM dd, yyyy") })
          }
          sx={{ width: 1 }}
        />

        <TagsAutocomplete
          tags={tags}
          onChange={(value) => {
            handleChange({ tags: value });
          }}
        />

        <Stack
          width={1}
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          <Button
            variant="outlined"
            endIcon={section ? <Edit /> : <Add />}
            fullWidth
            size="small"
            onClick={handleCreateSection}
          >
            {section ? "Edit section" : "Create Section"}
          </Button>

          <LoadingButton
            variant="contained"
            sx={{
              alignItems: "flex-end",
            }}
            fullWidth
            size="small"
            onClick={handleAddBlog}
          >
            Add blog
          </LoadingButton>
        </Stack>
      </Stack>
    </Container>
  );
};
